import React, { useContext } from 'react';
import { useForm } from '../Form';
import Form from '../Form/Form';
import Field from '../Field';
import styled, { keyframes } from 'styled-components';
import { productListingCardsStyles } from '../../styles/productListingCards';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import workflowService from '../../services/workflowService';
import { useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const StyledChatPanel = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
  .main-search {
    width: 100%;
    position: relative;
    border: 1px solid #2A2A30;
    background: var(--paper);
  }

  .text-block {
    flex-grow: 1;
    min-height: 0;
    overflow: auto;

    .ai-message-content {
      width: 100%;
    }

    ${productListingCardsStyles}

    .user-text {
      width: max-content;
      max-width: 250px;
      position: relative;
      background: rgba(255, 255, 255, 0.08);
      padding: 8px;
      border-radius: 8px;
      margin-left: auto !important;
    }
    .heading {
      margin: 0 0 10px;
      position: relative;
      line-height: 28px;
      a {
        text-decoration: underline;
        color: var(--primary-50);
      }

      table {
        border-collapse: collapse;
        width: max-content;
      }
      th,
      td {
        border: 1px solid #b1b1b1;
        border-spacing: 0;
        padding: 5px;
        text-align: left;
      }

      tr {
        &:first-child {
          background-color: #e8e8e8;
        }
        &:nth-child(even) {
          background-color: #eeeeee;
        }
      }

      .lucide-download {
        display: none;
      }

      .title {
        display: block;
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        margin: 0;
      }
    }
  }

  .chat-loader-text {
    display: block;
    font-weight: 400;
    margin-bottom: 10px;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }
`;

const ChatPanel = () => {
  const [form] = useForm();
  const { id } = useParams();
  const {
    workflowChatMessages,
    updateWorkflowUserChat,
    updateWorkflowBotChat,
    updateChatLoaderMessage,
    fakeAiLoader,
    fakeAiLoaderClear,
    chatLoaderMessages,
  } = useContext(TemporaryChatContext);

  const handleSave = async e => {
    try {
      updateWorkflowUserChat(e?.query);
      fakeAiLoader();
      form.resetForm();

      const aiResp = await workflowService.createWorkflowChat({
        workflow_id: id,
        query: e?.query,
      });

      const reader = aiResp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function extractJsonObjects(buffer) {
        const objects = [];
        let depth = 0;
        let start = 0;

        for (let i = 0; i < buffer.length; i++) {
          if (buffer[i] === '{') {
            if (depth === 0) start = i;
            depth++;
          } else if (buffer[i] === '}') {
            depth--;
            if (depth === 0) {
              objects.push(buffer.slice(start, i + 1));
            }
          }
        }

        const last = objects.length ? buffer.slice(buffer.lastIndexOf('}') + 1) : buffer;
        return { objects, leftover: last };
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const { objects, leftover } = extractJsonObjects(buffer);

        for (let jsonString of objects) {
          try {
            const parsed = JSON.parse(jsonString);
            const { type, data } = parsed;

            if (type === 'intermediate_step') {
              updateChatLoaderMessage(data);
              updateWorkflowBotChat('\n \n');
            }

            if (type === 'content') updateWorkflowBotChat(data);
            // if (type === 'tool_image_result') updateToolImages(data);
            // if (type === 'tool_result') {
            //   const fixed = parsePossiblyBrokenJson(data);
            //   updateChatSource(fixed?.search_results);
            // }
          } catch (err) {
            console.warn('Failed to parse chunk:', jsonString, err);
          }
        }

        buffer = leftover; // Keep incomplete JSON for next chunk
      }

      // ✅ Final leftover
      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer);
          const { type, data } = parsed;

          if (type === 'intermediate_step') updateChatLoaderMessage(data);
          if (type === 'content') updateWorkflowBotChat(data);
          //   if (type === 'tool_image_result') updateToolImages(data);
          //   if (type === 'tool_result') {
          //     const fixed = parsePossiblyBrokenJson(data);
          //     updateChatSource(fixed?.search_results);
          //   }
        } catch (err) {
          console.warn('Failed final parse:', buffer, err);
        }

        updateChatLoaderMessage('');
      }
    } catch (error) {
      fakeAiLoaderClear();
    }
  };

  return (
    <StyledChatPanel form={form} onSubmit={handleSave}>
      <div className="p-4 flex-1 text-block">
        {workflowChatMessages?.map((elem, ind) => {
          return (
            <div key={ind}>
              {elem?.human && (
                <div className="heading user-text">
                  <strong className="title">{elem?.human}</strong>
                </div>
              )}

              {elem?.ai && elem?.ai !== '' && (
                <div className="ai-message-content">
                  <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {elem?.ai}
                  </Markdown>
                </div>
              )}

              {elem?.isLoading && (
                <>
                  {chatLoaderMessages?.newChunk?.tool_name ? (
                    <strong className="chat-loader-text">{chatLoaderMessages?.newChunk?.tool_name}...</strong>
                  ) : (
                    <div style={{ width: '100%' }}>
                      <strong className="chat-loader-text">Thinking...</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-4 border-t border-[#2A2A30]">
        <Form.Item className="main-search" name="query" type="search" placeholder="How can I help you?" noMargin>
          <Field />
        </Form.Item>
      </div>
    </StyledChatPanel>
  );
};

export default ChatPanel;
